import React, { useEffect, useState } from 'react'
import api from '../api/axios'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { Download, RefreshCw, Package, CheckCircle, Clock } from 'lucide-react'
import { useAuth } from '../context/AuthContext'

const STATUS_COLORS: Record<string, string> = {
  'Completed': 'bg-green-100 text-green-700',
  'Unallocated': 'bg-gray-100 text-gray-600',
  'Allocated': 'bg-blue-100 text-blue-700',
  'In Process': 'bg-orange-100 text-orange-700',
  'Send to QC': 'bg-purple-100 text-purple-700',
  'Correction': 'bg-red-100 text-red-700',
  'Paused': 'bg-yellow-100 text-yellow-700',
  'Dismissed': 'bg-gray-100 text-gray-400',
  'Carryover': 'bg-teal-100 text-teal-700',
  'Query Sent': 'bg-indigo-100 text-indigo-700',
}

export default function MyReportsPage() {
  const { user } = useAuth()
  const [jobs, setJobs] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [client, setClient] = useState<any>(null)
  const [subscriptions, setSubscriptions] = useState<any[]>([])
  const [selectedSubId, setSelectedSubId] = useState<string>('custom')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const limit = 20

  useEffect(() => {
    // Fetch client info and subscriptions
    Promise.all([
      api.get('/clients/me'),
      api.get('/clients/me/subscriptions')
    ]).then(([clientRes, subRes]) => {
      setClient(clientRes.data)
      setSubscriptions(subRes.data)
      // Default to active subscription period if exists
      const activeSub = subRes.data.find((s: any) => s.status === 'active')
      if (activeSub) {
        setSelectedSubId(activeSub.subscriptionId)
        setDateFrom(activeSub.startDate.split('T')[0])
        setDateTo(activeSub.endDate.split('T')[0])
      }
    }).catch(() => {})
  }, [])

  useEffect(() => {
    fetchJobs()
  }, [page, statusFilter, typeFilter])

  const fetchJobs = async () => {
    setLoading(true)
    try {
      let url = `/jobs?page=${page}&limit=${limit}`
      if (statusFilter === 'Completed') url += `&status=Completed`
      else if (statusFilter === 'In Progress') url += `&status=In Process`
      if (typeFilter !== 'all') url += `&requestType=${typeFilter}`
      if (dateFrom) url += `&dateFrom=${dateFrom}`
      if (dateTo) url += `&dateTo=${dateTo}`
      if (search) url += `&search=${encodeURIComponent(search)}`
      const res = await api.get(url)
      setJobs(res.data.jobs || [])
      setTotal(res.data.pagination?.total || 0)
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  const handleSubChange = (subId: string) => {
    setSelectedSubId(subId)
    setPage(1)
    if (subId === 'custom') {
      setDateFrom('')
      setDateTo('')
    } else if (subId === 'all') {
      setDateFrom('')
      setDateTo('')
    } else {
      const sub = subscriptions.find(s => s.subscriptionId === subId)
      if (sub) {
        setDateFrom(sub.startDate.split('T')[0])
        setDateTo(sub.endDate.split('T')[0])
      }
    }
  }

  const handleRunReport = () => {
    setPage(1)
    fetchJobs()
  }

  const handleExportPDF = async () => {
    try {
      let url = `/jobs?page=1&limit=1000`
      if (statusFilter === 'Completed') url += `&status=Completed`
      else if (statusFilter === 'In Progress') url += `&status=In Process`
      if (typeFilter !== 'all') url += `&requestType=${typeFilter}`
      if (dateFrom) url += `&dateFrom=${dateFrom}`
      if (dateTo) url += `&dateTo=${dateTo}`
      if (search) url += `&search=${encodeURIComponent(search)}`
      const res = await api.get(url)
      const allJobs = res.data.jobs || []

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
      const pageWidth = doc.internal.pageSize.getWidth()

      // Load and add logo
      const logoUrl = '/New Inkcredible Logo.webp'
      const { imgData, imgAspect } = await new Promise<{ imgData: string, imgAspect: number }>((resolve) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          const canvas = document.createElement('canvas')
          canvas.width = img.width
          canvas.height = img.height
          const ctx = canvas.getContext('2d')!
          ctx.drawImage(img, 0, 0)
          resolve({ imgData: canvas.toDataURL('image/png'), imgAspect: img.height / img.width })
        }
        img.src = logoUrl
      })
      const logoImg = imgData

      // Header background
      doc.setFillColor(7, 24, 41)
      doc.rect(0, 0, pageWidth, 32, 'F')

      // Logo — correct aspect ratio from actual image dimensions
      const logoW = 55
      const logoH = logoW * imgAspect
      doc.addImage(logoImg, 'PNG', 6, 4, logoW, logoH)

      // Report title stacked below logo
      doc.setTextColor(200, 215, 230)
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.text('My Job Report', 8, 27)

      // Date range
      const periodText = dateFrom && dateTo
        ? `Period: ${new Date(dateFrom).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} — ${new Date(dateTo).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
        : 'Period: All Time'
      doc.setTextColor(150, 170, 190)
      doc.setFontSize(8)
      doc.text(periodText, pageWidth - 10, 13, { align: 'right' })

      const generatedOn = `Generated: ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
      doc.text(generatedOn, pageWidth - 10, 20, { align: 'right' })

      // Summary stats
      const completed = allJobs.filter((j: any) => j.status === 'Completed').length
      const inProgress = allJobs.filter((j: any) => j.status !== 'Completed' && j.status !== 'Dismissed').length
      const fresh = allJobs.filter((j: any) => j.requestType === 'fresh').length
      const revision = allJobs.filter((j: any) => j.requestType === 'revision').length

      const stats = [
        { label: 'Total Jobs', value: allJobs.length },
        { label: 'Completed', value: completed },
        { label: 'In Progress', value: inProgress },
        { label: 'Fresh', value: fresh },
        { label: 'Revision', value: revision },
      ]

      // Stats row
      let statX = 14
      const statY = 42
      const statW = (pageWidth - 28) / stats.length

      stats.forEach(({ label, value }) => {
        doc.setFillColor(17, 34, 64)
        doc.roundedRect(statX, statY - 6, statW - 3, 16, 2, 2, 'F')
        doc.setTextColor(255, 127, 50)
        doc.setFontSize(14)
        doc.setFont('helvetica', 'bold')
        doc.text(String(value), statX + (statW - 3) / 2, statY + 2, { align: 'center' })
        doc.setTextColor(120, 140, 160)
        doc.setFontSize(7)
        doc.setFont('helvetica', 'normal')
        doc.text(label, statX + (statW - 3) / 2, statY + 7, { align: 'center' })
        statX += statW
      })

      // Jobs table
      autoTable(doc, {
        startY: 66,
        head: [['Job Code', 'Title', 'Type', 'Status', 'Date']],
        body: allJobs.map((j: any) => [
          j.jobCode,
          j.displayTitle || '—',
          j.requestType,
          j.status,
          new Date(j.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
        ]),
        headStyles: {
          fillColor: [7, 24, 41],
          textColor: [255, 127, 50],
          fontStyle: 'bold',
          fontSize: 8,
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [30, 50, 80],
        },
        alternateRowStyles: {
          fillColor: [240, 245, 255],
        },
        columnStyles: {
          0: { cellWidth: 32 },
          1: { cellWidth: 70 },
          2: { cellWidth: 22 },
          3: { cellWidth: 28 },
          4: { cellWidth: 28 },
        },
        margin: { left: 14, right: 14 },
        didDrawPage: (data: any) => {
          // Footer on each page
          const pageCount = (doc as any).internal.getNumberOfPages()
          doc.setFontSize(7)
          doc.setTextColor(150, 165, 180)
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            pageWidth / 2,
            doc.internal.pageSize.getHeight() - 8,
            { align: 'center' }
          )
          doc.text(
            'Inkcredible — Confidential',
            14,
            doc.internal.pageSize.getHeight() - 8
          )
        }
      })

      const dateStr = new Date().toISOString().split('T')[0]
      doc.save(`my-jobs-${dateStr}.pdf`)
    } catch (e) {
      console.error(e)
    }
  }

  const isSubscriptionClient = subscriptions.length > 0
  const completed = jobs.filter(j => j.status === 'Completed').length
  const inProgress = jobs.filter(j => j.status !== 'Completed' && j.status !== 'Dismissed').length

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-navy-900">My Reports</h1>
          <p className="text-gray-400 mt-1 text-sm">View and export your job history</p>
        </div>
        <button onClick={handleExportPDF}
          className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2.5 rounded-xl font-medium transition-colors">
          <Download size={15} /> Export PDF
        </button>
      </div>

      {/* Filters */}
      <div className="card p-5 space-y-4">
        {/* Period selector — subscription clients get dropdown */}
        {isSubscriptionClient ? (
          <div>
            <label className="label">Subscription Period</label>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => handleSubChange('all')}
                className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${selectedSubId === 'all' ? 'bg-navy-800 text-white border-navy-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                All Time
              </button>
              {subscriptions.map((s: any) => (
                <button key={s.subscriptionId} onClick={() => handleSubChange(s.subscriptionId)}
                  className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${selectedSubId === s.subscriptionId ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                  {new Date(s.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} — {new Date(s.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  {s.status === 'active' && <span className="ml-1.5 bg-green-100 text-green-600 px-1.5 py-0.5 rounded-full text-[10px] font-semibold">Active</span>}
                </button>
              ))}
              <button onClick={() => handleSubChange('custom')}
                className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${selectedSubId === 'custom' ? 'bg-navy-800 text-white border-navy-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                Custom Range
              </button>
            </div>
          </div>
        ) : null}

        {/* Date range — shown for non-sub clients always, for sub clients only when custom selected */}
        {(!isSubscriptionClient || selectedSubId === 'custom') && (
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="label">From</label>
              <input type="date" className="input text-sm" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            </div>
            <div>
              <label className="label">To</label>
              <input type="date" className="input text-sm" value={dateTo} onChange={e => setDateTo(e.target.value)} />
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label">Status</label>
            <select className="input text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="Completed">Completed</option>
              <option value="In Progress">In Progress</option>
            </select>
          </div>
          <div>
            <label className="label">Type</label>
            <select className="input text-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              <option value="all">All Types</option>
              <option value="fresh">Fresh</option>
              <option value="revision">Revision</option>
            </select>
          </div>
          <div>
            <label className="label">Search</label>
            <input type="text" className="input text-sm" placeholder="Job code or title..."
              value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleRunReport()} />
          </div>
          <button onClick={handleRunReport} disabled={loading}
            className="flex items-center gap-1.5 text-xs bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-3 py-2 rounded-lg font-medium transition-colors">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Loading...' : 'Apply'}
          </button>
          {(dateFrom || dateTo || statusFilter !== 'all' || typeFilter !== 'all' || search) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); setStatusFilter('all'); setTypeFilter('all'); setSearch(''); setSelectedSubId('all'); setPage(1) }}
              className="text-xs px-3 py-2 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors">
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card p-4 flex items-center gap-3">
          <div className="w-9 h-9 bg-navy-50 rounded-xl flex items-center justify-center">
            <Package size={16} className="text-navy-600" />
          </div>
          <div>
            <p className="text-xl font-display font-bold text-navy-900">{total}</p>
            <p className="text-xs text-gray-400">Total Jobs</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-9 h-9 bg-green-50 rounded-xl flex items-center justify-center">
            <CheckCircle size={16} className="text-green-600" />
          </div>
          <div>
            <p className="text-xl font-display font-bold text-navy-900">{completed}</p>
            <p className="text-xs text-gray-400">Completed</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-9 h-9 bg-orange-50 rounded-xl flex items-center justify-center">
            <Clock size={16} className="text-orange-600" />
          </div>
          <div>
            <p className="text-xl font-display font-bold text-navy-900">{inProgress}</p>
            <p className="text-xs text-gray-400">In Progress</p>
          </div>
        </div>
      </div>

      {/* Jobs Table */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <p className="font-display font-semibold text-navy-900">
            Jobs {dateFrom && dateTo ? `— ${new Date(dateFrom).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} to ${new Date(dateTo).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : '— All Time'}
          </p>
        </div>
        {loading ? (
          <div className="py-12 text-center text-gray-400 text-sm">Loading...</div>
        ) : jobs.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">No jobs found for this period</div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-gray-50/70">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Job</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Type</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {jobs.map((j: any) => (
                  <tr key={j.id} className="hover:bg-orange-50/30">
                    <td className="px-6 py-3.5">
                      <p className="font-medium text-navy-800">{j.displayTitle || j.jobCode}</p>
                      <p className="text-xs font-mono text-gray-400">{j.jobCode}</p>
                    </td>
                    <td className="px-6 py-3.5">
                      <span className="text-xs capitalize text-gray-500">{j.requestType}</span>
                    </td>
                    <td className="px-6 py-3.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${j.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                        {j.status === 'Completed' ? 'Completed' : 'In Progress'}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 text-xs text-gray-400">
                      {new Date(j.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {total > limit && (
              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-400">{total} total jobs — page {page} of {Math.ceil(total / limit)}</p>
                <div className="flex items-center gap-2">
                  <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
                    className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Previous</button>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">Go to</span>
                    <input type="number" min={1} max={Math.ceil(total / limit)} defaultValue={page} key={page}
                      onKeyDown={e => { if (e.key === 'Enter') { const v = parseInt((e.target as HTMLInputElement).value); if (v >= 1 && v <= Math.ceil(total / limit)) setPage(v) }}}
                      onBlur={e => { const v = parseInt(e.target.value); if (v >= 1 && v <= Math.ceil(total / limit)) setPage(v) }}
                      className="w-14 text-xs text-center border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-orange-400" />
                    <span className="text-xs text-gray-400">of {Math.ceil(total / limit)}</span>
                  </div>
                  <button disabled={page * limit >= total} onClick={() => setPage(p => p + 1)}
                    className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
